import { Toggle } from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { CampaignDTO } from 'components/breadcrumb-bar/campaign-status'
import { useEffect, useState } from 'react'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { ignoredCampaignIds } from './connection-helpers'
import { setCampaignDetection } from './ignored-campaigns'

/**
 * Per-campaign click detection (DEV-2422 §2.3).
 *
 * The same ignored-campaigns list the ad-account drawer used to edit, inverted: OFF adds
 * the campaign to `preferences.ignored_campaigns`, ON removes it. No backend concept
 * changes — this is a different entry point into the same list.
 */
export const CampaignDetectionToggle = ({
	adAccount,
	campaign,
	canWrite
}: {
	adAccount: AdAccountDTO
	campaign: CampaignDTO
	canWrite: boolean
}) => {
	const { demoMode, refreshAdAccounts } = useAssetStore(
		useShallow((state) => ({
			demoMode: state.demoMode,
			refreshAdAccounts: state.refreshAdAccounts
		}))
	)

	const ignored = ignoredCampaignIds(adAccount)
	const [active, setActive] = useState(!ignored.includes(campaign.id))
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		setActive(!ignoredCampaignIds(adAccount).includes(campaign.id))
	}, [adAccount, campaign.id])

	const handleToggle = async () => {
		const next = !active
		// Optimistic, reverted on failure — the same shape as `ProtectionToggleCell`.
		setActive(next)
		setSaving(true)
		try {
			// One platform-agnostic write, queued per ad account: the endpoint replaces
			// the whole list, and this row cannot see what a neighbouring row is sending.
			// The per-platform settings PATCH is not usable here either — each requires
			// its whole payload, which this toggle does not hold.
			await setCampaignDetection(
				adAccount,
				campaign.id,
				next,
				refreshAdAccounts
			)
		} catch {
			setActive(!next)
		} finally {
			setSaving(false)
		}
	}

	return (
		<div className="flex items-center gap-2">
			<Toggle
				id={`campaign-detection-${campaign.id}`}
				size="md"
				checked={active}
				disabled={demoMode || !canWrite || saving}
				onChange={handleToggle}
			/>
			<span className="text-sm text-t-secondary">{active ? 'On' : 'Off'}</span>
		</div>
	)
}
